"use client";

import { IconArrowRight, IconPhone, IconX } from "@tabler/icons-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { COMPANY } from "@/lib/constants";

// Passé ce délai sans interaction (survol, focus), le widget se referme
// tout seul et ne revient qu'au prochain chargement complet de la page.
const AUTO_CLOSE_MS = 12_000;

export function FloatingContact() {
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);
  const [autoClosed, setAutoClosed] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // Ref + état : la ref sécurise le callback du timer (pas de fermeture si
  // l'utilisateur vient de s'engager), l'état pilote la barre de progression.
  const engagedRef = useRef(false);
  const [engaged, setEngaged] = useState(false);

  useEffect(() => {
    // sessionStorage peut lever une SecurityError si le stockage est bloqué
    // (Chrome "Bloquer tous les cookies"). Monté dans le root layout : une
    // exception ici démonterait tout l'arbre client.
    try {
      if (sessionStorage.getItem("fc-dismissed") === "1") setDismissed(true);
    } catch {
      // Stockage indisponible : le widget réapparaît simplement à chaque page.
    }
  }, []);

  useEffect(() => {
    function onScroll() {
      setVisible(window.scrollY > window.innerHeight * 0.6);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const show = visible && !dismissed && !autoClosed;

  useEffect(() => {
    if (!show || engaged) return;
    const id = window.setTimeout(() => {
      if (!engagedRef.current) setAutoClosed(true);
    }, AUTO_CLOSE_MS);
    return () => {
      window.clearTimeout(id);
    };
  }, [show, engaged]);

  const engage = () => {
    engagedRef.current = true;
    setEngaged(true);
  };

  if (pathname === "/contact" || pathname === "/login" || pathname.startsWith("/dashboard")) {
    return null;
  }

  return (
    <aside
      aria-label="Rappel de contact"
      inert={!show}
      className={`fixed z-50 bottom-4 inset-x-4 md:inset-x-auto md:right-6 md:bottom-6 md:w-[340px] transition-all duration-500 ease-out ${
        show ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0 pointer-events-none"
      }`}
    >
      <div
        onPointerEnter={engage}
        onFocusCapture={engage}
        className="relative mx-auto max-w-md md:max-w-none bg-surface/95 backdrop-blur-md border border-(--primary-light)/25 rounded-sm p-5 md:p-6 shadow-[0_15px_40px_-15px_rgba(31,41,32,0.18),0_0_0_1px_rgba(66,119,83,0.08)_inset] overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(ellipse 70% 100% at 100% 0%, rgba(66,119,83,0.10) 0%, transparent 70%)",
          }}
        />

        <button
          onClick={() => {
            setDismissed(true);
            try {
              sessionStorage.setItem("fc-dismissed", "1");
            } catch {
              // Stockage indisponible : fermeture non persistée, sans erreur.
            }
          }}
          type="button"
          aria-label="Fermer"
          className="absolute top-3 right-3 p-1 text-text-muted hover:text-text-primary transition-colors z-10"
        >
          <IconX size={14} stroke={1.75} />
        </button>

        <div className="relative">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-text-muted mb-3">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary-light opacity-60 animate-ping" />
              <span className="relative rounded-full h-1.5 w-1.5 bg-primary-light" />
            </span>
            <span>Disponibles du lundi au vendredi</span>
          </div>

          <p
            className="text-xl md:text-[22px] tracking-tight text-text-primary leading-[1.15]"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Un projet ? Parlons-en.
          </p>
          <p className="mt-1.5 text-xs text-text-muted">Devis gratuit, sans engagement.</p>

          <div className="mt-5 flex flex-col gap-2.5">
            <a
              href={`tel:${COMPANY.phoneHref}`}
              className="group inline-flex items-center justify-between gap-3 px-4 py-3 bg-primary hover:bg-primary-light text-white rounded-full transition-colors"
            >
              <span className="flex items-center gap-2.5">
                <IconPhone size={17} stroke={1.75} aria-hidden />
                <span className="text-[15px] font-medium tabular-nums">{COMPANY.phoneDisplay}</span>
              </span>
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/80 group-hover:text-white transition-colors">
                Appeler
              </span>
            </a>

            <Link
              href="/contact"
              className="group inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm text-(--text-primary)/85 hover:text-primary-light transition-colors border-t border-border pt-3"
            >
              <span>Ou envoyer un message</span>
              <IconArrowRight
                size={14}
                stroke={1.75}
                className="group-hover:translate-x-0.5 transition-transform"
                aria-hidden
              />
            </Link>
          </div>
        </div>

        <div
          aria-hidden
          className={`absolute inset-x-0 bottom-0 h-[2.5px] bg-(--primary-light)/12 transition-opacity duration-500 ${
            engaged ? "opacity-0" : "opacity-100"
          }`}
        >
          <div
            className="h-full w-full origin-left bg-linear-to-r from-(--primary-light)/45 to-(--primary-light)"
            style={{
              animation: show ? `fc-drain ${AUTO_CLOSE_MS}ms linear forwards` : "none",
              animationPlayState: engaged ? "paused" : "running",
            }}
          />
        </div>
      </div>
    </aside>
  );
}
